import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Container,
  InputAdornment,
  IconButton,
  LinearProgress,
  Fade,
  Zoom
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GoogleIcon from '@mui/icons-material/Google';
import AuthService from '../../services/AuthService';
import { useAuth } from '../../contexts/AuthContext';

const Signup: React.FC = () => {
  const { setUser } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const userStr = searchParams.get('user');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError('Google authentication failed. Please try again.');
      return;
    }

    if (userStr) {
      try {
        const user = JSON.parse(decodeURIComponent(userStr));
        AuthService.saveUser(user);
        setUser(user);
        navigate('/home');
      } catch (err) {
        setError('Failed to process authentication');
      }
    }
  }, [searchParams, navigate, setUser]);

  const passwordStrength = password.length > 0 ? Math.min((password.length / 8) * 100, 100) : 0;
  const passwordRequirements = {
    length: password.length >= 6,
    strong: password.length >= 8
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      const response = await AuthService.signup({ username, email, password });
      if (response.success) {
        AuthService.saveUser(response.data.user);
        setUser(response.data.user);
        navigate('/home');
      } else {
        setError(response.message || 'Signup failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 3 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.08) 0%, transparent 50%)',
          animation: 'float 20s ease-in-out infinite'
        }
      }}
    >
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(30px, -30px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
          }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, pt: { xs: 8, md: 10 } }}>
        {/* Back Button */}
        <Fade in timeout={400}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              position: 'absolute',
              top: { xs: 16, md: 24 },
              left: { xs: 0, md: 0 },
              color: 'white',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 2,
              px: 2,
              py: 1,
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              textTransform: 'none',
              zIndex: 2,
              '&:hover': {
                background: 'rgba(255,255,255,0.25)',
                borderColor: 'rgba(255,255,255,0.3)',
                transform: 'translateX(-4px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Back
          </Button>
        </Fade>

        {/* Main Card */}
        <Zoom in timeout={600}>
          <Card
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 480,
              mx: 'auto',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(255,255,255,0.2)',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(250,250,252,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 8px 24px rgba(102, 126, 234, 0.2)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 5,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
              }
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
              {/* Logo and Header */}
              <Fade in timeout={800}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box
                    component="img"
                    src="/images/logo.png"
                    alt="OpinLoop"
                    sx={{
                      height: { xs: 64, sm: 72, md: 80 },
                      width: 'auto',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: 2,
                      mb: 3
                    }}
                  />
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={700} 
                    gutterBottom
                    sx={{ 
                      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                      color: 'text.primary',
                      mb: 1
                    }}
                  >
                    Join OpinLoop
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: { xs: '0.9375rem', sm: '1rem' },
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    Create your account and start sharing your perspective
                  </Typography>
                </Box>
              </Fade>

              {error && (
                <Fade in timeout={400}>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      fontWeight: 500
                    }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    variant="outlined"
                    required
                    disabled={loading}
                    sx={{
                      mb: 2.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.9375rem', sm: '1rem' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 22 } }} />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    fullWidth
                    type="email"
                    label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="outlined"
                    required
                    disabled={loading}
                    sx={{
                      mb: 2.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.9375rem', sm: '1rem' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 22 } }} />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="outlined"
                    required
                    disabled={loading}
                    sx={{
                      mb: password.length > 0 ? 1.5 : 2.5,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.9375rem', sm: '1rem' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 22 } }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'primary.main'
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  
                  {password.length > 0 && (
                    <Box sx={{ mb: 2.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          mb: 1.5,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            bgcolor: passwordStrength >= 75 ? 'success.main' : passwordStrength >= 50 ? 'warning.main' : 'error.main',
                            boxShadow: passwordStrength >= 75 
                              ? '0 2px 8px rgba(76, 175, 80, 0.3)' 
                              : passwordStrength >= 50 
                              ? '0 2px 8px rgba(255, 152, 0, 0.3)' 
                              : '0 2px 8px rgba(244, 67, 54, 0.3)'
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {passwordRequirements.length ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', borderColor: 'divider' }} />
                          )}
                          <Typography 
                            variant="caption" 
                            color={passwordRequirements.length ? 'success.main' : 'text.secondary'}
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                              fontWeight: passwordRequirements.length ? 600 : 500
                            }}
                          >
                            Min 6 characters
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {passwordRequirements.strong ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', borderColor: 'divider' }} />
                          )}
                          <Typography 
                            variant="caption" 
                            color={passwordRequirements.strong ? 'success.main' : 'text.secondary'}
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                              fontWeight: passwordRequirements.strong ? 600 : 500
                            }}
                          >
                            Strong (8+ characters)
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    variant="outlined"
                    required
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                          boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.9375rem', sm: '1rem' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 22 } }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            size="small"
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'primary.main'
                              }
                            }}
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    error={confirmPassword.length > 0 && password !== confirmPassword}
                    helperText={confirmPassword.length > 0 && password !== confirmPassword ? 'Passwords do not match' : ''}
                  />
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  size="large"
                  sx={{
                    mb: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.0625rem' },
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.35), 0 2px 8px rgba(102, 126, 234, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                      boxShadow: '0 10px 32px rgba(102, 126, 234, 0.45), 0 4px 12px rgba(102, 126, 234, 0.3)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      opacity: 0.6
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                  <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      px: 2,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      fontWeight: 500
                    }}
                  >
                    OR
                  </Typography>
                  <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {}}
                  disabled={true}
                  startIcon={<GoogleIcon />}
                  size="large"
                  sx={{
                    mb: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                    textTransform: 'none',
                    borderColor: '#db4437',
                    color: '#db4437',
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    '&:hover': {
                      borderColor: '#db4437',
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  Continue with Google
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 1,
                      px: 1.5,
                      py: 0.5,
                      bgcolor: 'warning.main',
                      color: 'white',
                      borderRadius: 1,
                      fontSize: '0.6875rem',
                      fontWeight: 700
                    }}
                  >
                    Coming Soon
                  </Typography>
                </Button>

                <Box textAlign="center">
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                    }}
                  >
                    Already have an account?{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={() => navigate('/login')}
                      sx={{
                        fontWeight: 700,
                        textDecoration: 'none',
                        color: 'primary.main',
                        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.dark'
                        },
                        transition: 'color 0.2s ease'
                      }}
                    >
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              </form>

              {/* Releasepilot Branding */}
              <Box 
                sx={{ 
                  mt: 4, 
                  pt: 3, 
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}
              >
                <Link
                  href="https://releasepilot.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    textDecoration: 'none',
                    color: 'text.secondary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src="/images/rp_icon.png"
                    alt="Releasepilot"
                    sx={{
                      height: { xs: 20, sm: 24 },
                      width: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      fontWeight: 500
                    }}
                  >
                    Part of Releasepilot
                  </Typography>
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Zoom>
      </Container>
    </Box>
  );
};

export default Signup;
